/**
 * Login flow — builds login/logout URLs and exchanges auth code for tokens.
 */
import { AUTH_CONFIG } from './config';
import { createPkceChallenge, consumePkceVerifier } from './pkce';
import { getRefreshToken, setToken, clearToken } from './token';

/** Build the Cognito login URL with PKCE challenge. */
export const buildLoginUrl = async (): Promise<string> => {
  const challenge = await createPkceChallenge();
  return (
    `https://${AUTH_CONFIG.domain}/oauth2/authorize?` +
    `client_id=${AUTH_CONFIG.clientId}` +
    `&response_type=code` +
    `&scope=${AUTH_CONFIG.scopes}` +
    `&redirect_uri=${encodeURIComponent(AUTH_CONFIG.redirectUri)}` +
    `&code_challenge=${challenge}` +
    `&code_challenge_method=S256`
  );
};

/** Build the Cognito logout URL. */
export const buildLogoutUrl = (): string =>
  `https://${AUTH_CONFIG.domain}/logout?` +
  `client_id=${AUTH_CONFIG.clientId}` +
  `&logout_uri=${encodeURIComponent(AUTH_CONFIG.logoutUri)}`;

/** Exchange an authorization code for tokens. Throws on failure. */
export const exchangeCode = async (code: string): Promise<{ idToken: string; refreshToken?: string }> => {
  const verifier = consumePkceVerifier();
  if (!verifier) {
    throw new Error('PKCE verifier not found — login flow may have been interrupted.');
  }

  const res = await fetch(`https://${AUTH_CONFIG.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: AUTH_CONFIG.clientId,
      code,
      redirect_uri: AUTH_CONFIG.redirectUri,
      code_verifier: verifier,
    }),
  });

  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status}`);
  }

  const data = await res.json();
  return { idToken: data.id_token, refreshToken: data.refresh_token };
};

// A single shared refresh so concurrent API calls trigger at most one network refresh.
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Silently obtain a fresh id_token using the stored refresh token (no page reload).
 * Returns the new id_token, or null if there's no refresh token or the refresh fails
 * (e.g. the refresh token itself expired) — callers then fall back to re-login.
 */
export const refreshIdToken = (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async (): Promise<string | null> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;
    try {
      const res = await fetch(`https://${AUTH_CONFIG.domain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: AUTH_CONFIG.clientId,
          refresh_token: refreshToken,
        }),
      });
      if (!res.ok) {
        clearToken(); // refresh token rejected/expired — force a clean re-login
        return null;
      }
      const data = await res.json();
      if (!data.id_token) return null;
      setToken(data.id_token);
      return data.id_token;
    } catch {
      return null; // network error — leave tokens in place, caller can retry
    }
  })().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
};
